import { useState } from 'react';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { Inline } from '../atoms/Inline/Inline';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { Section } from '../templates/Section/Section';
import { useTranslation } from '../../i18n/useTranslation';
import { useVoiceConsentMutations, useVoiceSettings } from '../../hooks/useCaptures';
import { useOptionalCapture } from '../capture/CaptureContext';

export interface VoiceNotesSectionProps {
  className?: string;
}

/**
 * Settings → voice notes (privacy review D3): the state of the voice-note
 * consent, allow it (opens the consent dialog) or withdraw it at any time —
 * recording is blocked again until it is given. Not rendered while loading, or
 * when voice notes are switched off and no consent was ever given.
 */
export function VoiceNotesSection({ className = '' }: VoiceNotesSectionProps) {
  const { t, i18n } = useTranslation();
  const settings = useVoiceSettings();
  const { withdraw } = useVoiceConsentMutations();
  const capture = useOptionalCapture();
  const [withdrawn, setWithdrawn] = useState(false);

  const data = settings.data;
  if (!data || (!data.enabled && !data.consent.given)) return null;

  const givenOn = data.consent.givenAt
    ? new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(data.consent.givenAt))
    : null;

  const handleWithdraw = (): void => {
    setWithdrawn(false);
    withdraw.mutate(undefined, { onSuccess: () => setWithdrawn(true) });
  };

  return (
    <Section title={t('settings.voice.title')} className={className}>
      <Card padding="lg">
        <Stack gap="4">
          <Text>{t('settings.voice.intro')}</Text>
          <Text size="sm" color="secondary">
            {data.consent.given ? t('settings.voice.statusGiven', { date: givenOn }) : t('settings.voice.statusNotGiven')}
          </Text>
          {withdrawn && (
            <LiveRegion>
              <Text size="sm">{t('settings.voice.withdrawn')}</Text>
            </LiveRegion>
          )}
          {withdraw.isError && <Alert tone="danger">{t('settings.voice.saveError')}</Alert>}
          <Inline justify="end">
            {data.consent.given ? (
              <Button variant="secondary" onClick={handleWithdraw} isLoading={withdraw.isPending}>
                {t('settings.voice.withdraw')}
              </Button>
            ) : (
              capture?.available && (
                <Button variant="secondary" onClick={() => capture.openConsent()}>
                  {t('settings.voice.allow')}
                </Button>
              )
            )}
          </Inline>
        </Stack>
      </Card>
    </Section>
  );
}
